import {AfterViewInit, Component, inject, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {LoggingService} from "../../services/logger/logging.service";
import {SizeProp} from "@fortawesome/fontawesome-svg-core";
import {AccValidator} from "../utils/validator";

import * as solid from '@fortawesome/free-solid-svg-icons';
import * as regular from '@fortawesome/free-regular-svg-icons';
import * as brand from '@fortawesome/free-brands-svg-icons';
import {FaIconComponent, IconDefinition} from "@fortawesome/angular-fontawesome";
import {isDefined, isFalse} from "../utils/epic.utils";
import {Size} from "../../base/data/size";

@Component({
  selector: 'acc-icon',
  imports: [
    FaIconComponent
  ],
  templateUrl: './icon.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './icon.component.css'
})
export class IconComponent implements OnInit, AfterViewInit {

  public static readonly LINK_EXTERNAL_HTML: string = '<svg role="img" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="up-right-from-square" class="svg-inline--fa fa-up-right-from-square fa-xxs ms-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M352 0c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9L370.7 96 201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L416 141.3l41.4 41.4c9.2 9.2 22.9 11.9 34.9 6.9s19.8-16.6 19.8-29.6l0-128c0-17.7-14.3-32-32-32L352 0zM80 32C35.8 32 0 67.8 0 112L0 432c0 44.2 35.8 80 80 80l320 0c44.2 0 80-35.8 80-80l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 112c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-320c0-8.8 7.2-16 16-16l112 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 32z"></path></svg>';

  private static readonly CUSTOM_ICON: string = 'custom';
  private static readonly FA_ICON_PREFIX: string[] = ['fas', 'far', 'fab'];

  @Input({required: true})
  public name!: string;

  @Input()
  public size: string | number | Size | undefined | 'xxs' | 'xs' | 'lg' | 'xl' | 'sm' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 = 1;

  @Input()
  public styling: string | 'help' | 'warning' | 'danger' | 'success' | 'disabled' | 'primary' | undefined = undefined;

  protected faIcon: any;

  private readonly log: LoggingService = inject(LoggingService);

  private readonly defaultPrime: IconDefinition = solid.faStar;
  private readonly defaultClean: IconDefinition = solid.faXmark;
  private readonly defaultDelete: IconDefinition = solid.faTrash;
  private readonly defaultAdd: IconDefinition = solid.faPlus;
  private readonly defaultView: IconDefinition = solid.faEye;
  private readonly defaultEdit: IconDefinition = solid.faPen;
  private readonly defaultSearch: IconDefinition = solid.faSearch;
  private readonly defaultWait: IconDefinition = solid.faHourglass;
  private readonly defaultHelp: IconDefinition = solid.faCircleQuestion;
  private readonly defaultInfo: IconDefinition = solid.faCircleInfo;
  private readonly defaultWarn: IconDefinition = solid.faTriangleExclamation;
  private readonly defaultExample: IconDefinition = solid.faEye;
  private readonly defaultError: IconDefinition = solid.faCircleExclamation;

  private icons: Map<string, IconDefinition | string> = new Map<string, IconDefinition | string>([
    // Brand
    ['paypal', brand.faPaypal],
    ['cc-visa', brand.faCcVisa],
    ['cc-mastercard', brand.faCcMastercard],
    ['cc-diners', brand.faCcDinersClub],
    ['cc-amex', brand.faCcAmex],
    // Actions
    ['error', this.defaultError],
    ['dashboard', IconComponent.CUSTOM_ICON],
    ['sub-nav-arrow', IconComponent.CUSTOM_ICON],
    ['invoice', solid.faFileInvoiceDollar],
    ['invoice-draft', brand.faFirstdraft],
    ['invoice-order', solid.faFilePen],
    ['invoice-quotation', solid.faFileCircleExclamation],
    ['invoice-cancellation', solid.faFileCircleXmark],
    ['invoice-delivery-note', solid.faFileLines],
    ['invoice-proforma', solid.faFileLines],
    ['invoice-cost-estimate', solid.faFileInvoice],
    ['preview', regular.faEye],
    ['invoice-preview', regular.faEye],
    ['receipt', solid.faReceipt],
    ['receipt-reviewed', solid.faCircleCheck],
    ['receipt-unreviewed', solid.faMagnifyingGlass],
    ['notification', regular.faBell],
    ['org-switch', regular.faBuilding],
    ['org', regular.faBuilding],
    ['form-status', regular.faFloppyDisk],
    ['typeahead-clean', this.defaultClean],
    ['add', this.defaultAdd],
    ['view', this.defaultView],
    ['edit', this.defaultEdit],
    ['clone', solid.faClone],
    ['delete', this.defaultDelete],
    //['email', solid.faEnvelope],
    ['email', solid.faAt],
    ['email-add', this.defaultAdd],
    ['email-add-describer', solid.faAt],
    ['email-prime', this.defaultPrime],
    ['email-unverified', this.defaultWait],
    //['email-default', solid.faEnvelope],
    ['email-default', solid.faAt],
    ['email-delete', this.defaultDelete],
    ['email-save', solid.faCheck],
    ['address-prime', this.defaultPrime],
    ['address-default', solid.faLocationDot],
    ['phone-prime', this.defaultPrime],
    ['phone-default', solid.faPhone],
    ['hyperlink-prime', this.defaultPrime],
    ['hyperlink-default', solid.faGlobe],
    ['hyperlink-external', solid.faUpRightFromSquare],
    ['bank', solid.faBuildingColumns],
    ['bank-address-prime', this.defaultPrime],
    ['bank-address-default', regular.faCreditCard],
    ['help', this.defaultHelp],
    ['info', this.defaultInfo],
    ['warn', this.defaultWarn],
    ['example', this.defaultExample],
    ['org-use', solid.faPlay],
    ['contact', solid.faAddressBook],
    ['contact-person', solid.faUser], // Enum: ContactType
    ['contact-company', solid.faBuilding], // Enum: ContactType
    ['contact-company_contact', solid.faBuildingUser], // Enum: ContactType
    ['contact-company-contact', solid.faBuildingUser], // Fallback for 'contact-company_contact'
    ['contact-new', solid.faUserPlus],
    ['vehicle', solid.faCar],
    ['vehicle-add', solid.faCar],
    ['vehicle-inspection', solid.faGears],
    ['vehicle-tire', solid.faCarSide],
    ['article', solid.faTruck],
    ['catalog', solid.faBook],
    ['sort-asc', solid.faCaretUp],
    ['sort-desc', solid.faCaretDown],
    ['wait', this.defaultWait],
    ['search', this.defaultSearch],
    ['search-reset', solid.faTimes],
    ['search-reset-disabled', solid.faTimes],
    ['search-reset-all', regular.faCircleXmark],
    ['vat', solid.faFunnelDollar],
    ['settings', solid.faCog],
    ['datepicker', solid.faCalendarDays],
    ['copy', regular.faCopy],
    ['copy-success', regular.faCircleCheck],
    ['newsletter', regular.faEnvelopeOpen],
    ['keyboard', regular.faKeyboard],
    ['cashSale', solid.faMoneyBillWave],
    ['cancel', solid.faTimes],
    ['save', solid.faCheck],
    ['move-vertical', solid.faArrowsAltV],
    ['move-all', solid.faArrowsUpDownLeftRight],
    ['show', solid.faEye],
    ['media', solid.faFile],
    ['amount', solid.faWallet],
    ['backlink', solid.faRightToBracket],
    ['switch', solid.faRepeat],
    ['user', solid.faUser],
    ['group', solid.faUserGroup],
    ['new', solid.faStar],
    ['feature', solid.faShapes],
    ['wallet', solid.faWallet],
    ['template', solid.faAlignLeft],
    ['history', solid.faClockRotateLeft],
    ['statistic', solid.faChartLine],
    ['statistics', solid.faChartLine],
    ['success', regular.faCircleCheck],
    ['fail', regular.faCircleXmark],
    ['download', solid.faDownload],
    ['letter', solid.faEnvelopeOpenText],
    ['letter-calc-color', IconComponent.CUSTOM_ICON],
    ['letter-calc-greyscale', IconComponent.CUSTOM_ICON],
    ['letter-calc-duplex', IconComponent.CUSTOM_ICON],
    ['letter-calc-simplex', IconComponent.CUSTOM_ICON],
    ['correspondence', solid.faEnvelope],
    ['export', solid.faFileExport],
    ['comment', solid.faComment],
    ['comment-add', solid.faCommentMedical],
    ['approved', regular.faCircleCheck],
    ['location', solid.faLocationDot],
    ['tire-season-summer', solid.faSun], // Enum: TireSeason
    ['tire-season-winter', solid.faSnowflake], // Enum: TireSeason
    ['tire-season-all_season', solid.faCloudSunRain], // Enum: TireSeason
    ['tire-season-unknown', solid.faCircleQuestion], // Enum: TireSeason
    ['tire-switch', solid.faShuffle],
    ['windows', brand.faWindows],
    ['linux', brand.faLinux],
    ['apple', brand.faApple],
    ['mac', brand.faApple],
    ['number', solid.faHashtag],
    ['support', solid.faHeadset],
    ['reply', solid.faReply],
    ['close', solid.faCircleXmark],
    ['tombstone', IconComponent.CUSTOM_ICON],
    ['building', solid.faBuilding],
    ['relation', solid.faArrowRightArrowLeft],
    ['medical', solid.faHouseMedical],
    ['medical-house', solid.faHouseMedical],
    ['calculation', solid.faCalculator],
    ['goods-group', solid.faLayerGroup],
    ['file', solid.faFile],
    ['file-pdf', solid.faFilePdf],
    ['file-image', solid.faFileImage],
    ['file-csv', solid.faFileCsv],
    ['attachment', solid.faPaperclip],
    ['credit-card', solid.faCreditCard],
    ['affiliate', solid.faHandHoldingDollar],
    ['sheet', solid.faFilePen],
  ]);

  ngOnInit(): void {
    const icon: IconDefinition | string | undefined = this.icons.get(this.name);
    if (typeof icon === 'object') {
      this.faIcon = IconComponent.FA_ICON_PREFIX.includes(icon.prefix) ? icon : undefined;
    } else {
      this.faIcon = undefined;
    }
  }

  ngAfterViewInit(): void {
    if (isFalse(this.icons.has(this.name))) {
      this.log.error("Icon: name not found '{}'", [this.name]);
    }
  }

  protected getSize(): SizeProp {
    if (this.size instanceof Size) {
      return this.size.value as SizeProp;
    } else if (AccValidator.isNumeric(this.size)) {
      return this.size + 'x' as SizeProp;
    } else if (isDefined(this.size)) {
      return this.size as SizeProp;
    }
    return '1x' as SizeProp;
  }
}
